import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowLeft, Save, Loader2, Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { adminApi } from "../../lib/api";
import { Input } from "../../components/ui/input";
import { Textarea } from "../../components/ui/textarea";
import { Label } from "../../components/ui/label";
import { money } from "../../lib/jobCalc";

const inputCls = "bg-white border-[#cfd7db] h-10";

const Field = ({ label, hint, children, span = 1 }) => (
  <div className={`flex flex-col gap-1.5 ${span === 2 ? "sm:col-span-2" : ""}`}>
    <Label className="font-display uppercase tracking-widest text-xs text-[#3f4b52]">{label}</Label>
    {children}
    {hint && <div className="text-[11px] text-[#9aa6ad]">{hint}</div>}
  </div>
);

const todayIso = () => new Date().toISOString().slice(0, 10);

export const AdminCreditNoteForm = () => {
  const nav = useNavigate();
  const [customers, setCustomers] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    customer_id: "",
    job_id: "",
    date: todayIso(),
    reason: "",
    description: "",
    lines: [{ description: "", qty: 1, unit_price: 0 }],
    vat_pct: 15,
    vat_mode: "exclusive",
  });

  useEffect(() => {
    (async () => {
      try { setCustomers(await adminApi.listCustomers({ active: true })); }
      catch { toast.error("Could not load customers"); }
    })();
  }, []);

  useEffect(() => {
    if (!form.customer_id) { setJobs([]); return; }
    (async () => {
      try {
        const rows = await adminApi.listJobs({ customer_id: form.customer_id });
        setJobs(rows);
      } catch { setJobs([]); }
    })();
  }, [form.customer_id]);

  const totals = useMemo(() => {
    const net = (form.lines || []).reduce((a, l) => a + (Number(l.qty || 1) * Number(l.unit_price || 0)), 0);
    let sub, vat, gross;
    if (form.vat_mode === "none") { sub = net; vat = 0; gross = net; }
    else if (form.vat_mode === "inclusive") {
      const d = 1 + (Number(form.vat_pct || 0) / 100);
      gross = net; sub = d ? net / d : net; vat = gross - sub;
    } else {
      sub = net; vat = sub * (Number(form.vat_pct || 0) / 100); gross = sub + vat;
    }
    return { sub, vat, gross };
  }, [form.lines, form.vat_pct, form.vat_mode]);

  const setLine = (i, patch) =>
    setForm((f) => ({ ...f, lines: f.lines.map((l, idx) => idx === i ? { ...l, ...patch } : l) }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.customer_id) return toast.error("Pick a customer");
    if ((form.lines || []).length === 0 || !form.lines.some((l) => Number(l.unit_price) > 0)) {
      return toast.error("Add at least one line with a value");
    }
    setSaving(true);
    try {
      const cn = await adminApi.createCreditNote({
        ...form,
        job_id: form.job_id || null,
      });
      toast.success(`${cn.number} created`);
      nav(`/admin/credit-notes/${cn.id}`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not create credit note");
    } finally { setSaving(false); }
  };

  return (
    <form onSubmit={submit} data-testid="admin-credit-note-form">
      <Link to="/admin/credit-notes" className="inline-flex items-center gap-1 text-xs font-display uppercase tracking-widest text-[#3f4b52] hover:text-[#f28a16]">
        <ArrowLeft size={14} /> Back to credit notes
      </Link>
      <div className="flex items-start justify-between flex-wrap gap-3 mt-2">
        <h1 className="font-display font-black uppercase text-3xl sm:text-4xl text-[#111619]">New credit note</h1>
        <button
          type="submit"
          disabled={saving}
          data-testid="cn-save-btn"
          className="bg-[#f28a16] text-white px-6 h-11 rounded-md font-display uppercase tracking-widest text-sm inline-flex items-center gap-2 disabled:opacity-60"
        >
          {saving ? <Loader2 size={16} className="animate-spin" /> : <Save size={16} />}
          Create credit note
        </button>
      </div>

      <div className="mt-6 max-w-5xl space-y-6">
        <section className="bg-white border border-[#cfd7db] rounded-md p-6">
          <h2 className="font-display font-bold uppercase tracking-wider text-lg text-[#111619] border-l-4 border-[#f28a16] pl-3 mb-5">Header</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-5 gap-y-4">
            <Field label="Customer *">
              <select value={form.customer_id} onChange={(e) => setForm({ ...form, customer_id: e.target.value, job_id: "" })} className={`${inputCls} px-3`} data-testid="cn-customer">
                <option value="">Select a customer…</option>
                {customers.map((c) => <option key={c.id} value={c.id}>{c.code} — {c.name}</option>)}
              </select>
            </Field>
            <Field label="Against invoice / job (optional)" hint="Leave empty for a standalone credit note (goodwill, refund).">
              <select value={form.job_id} onChange={(e) => setForm({ ...form, job_id: e.target.value })} disabled={!form.customer_id} className={`${inputCls} px-3`} data-testid="cn-job">
                <option value="">Standalone (no invoice)</option>
                {jobs.map((j) => <option key={j.id} value={j.id}>{j.invoice_number || `#JOB${j.job_number}`} · {j.vehicle_make_model || j.registration || "job"}</option>)}
              </select>
            </Field>
            <Field label="Date *">
              <Input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} className={inputCls} />
            </Field>
            <Field label="Reason">
              <Input value={form.reason} onChange={(e) => setForm({ ...form, reason: e.target.value })} className={inputCls} placeholder="e.g. Goodwill discount / duplicate invoice" />
            </Field>
            <Field label="Description" span={2}>
              <Textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} rows={2} className="bg-white border-[#cfd7db]" />
            </Field>
            <Field label="VAT status">
              <select value={form.vat_mode} onChange={(e) => setForm({ ...form, vat_mode: e.target.value })} className={`${inputCls} px-3`}>
                <option value="exclusive">VAT exclusive</option>
                <option value="inclusive">VAT inclusive</option>
                <option value="none">No VAT</option>
              </select>
            </Field>
            <Field label="VAT %">
              <Input type="number" step="0.01" value={form.vat_pct} onChange={(e) => setForm({ ...form, vat_pct: Number(e.target.value || 0) })} className={inputCls} disabled={form.vat_mode === "none"} />
            </Field>
          </div>
        </section>

        <section className="bg-white border border-[#cfd7db] rounded-md p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-display font-bold uppercase tracking-wider text-lg text-[#111619] border-l-4 border-[#f28a16] pl-3">Lines</h2>
            <button
              type="button"
              onClick={() => setForm((f) => ({ ...f, lines: [...f.lines, { description: "", qty: 1, unit_price: 0 }] }))}
              className="text-[#f28a16] hover:underline text-xs font-display uppercase tracking-widest inline-flex items-center gap-1"
              data-testid="cn-add-line"
            >
              <Plus size={12} /> Add line
            </button>
          </div>
          <div className="space-y-2">
            {form.lines.map((l, i) => (
              <div key={i} className="grid grid-cols-12 gap-2 items-center">
                <Input value={l.description} onChange={(e) => setLine(i, { description: e.target.value })} placeholder="Description" className={`${inputCls} col-span-7`} />
                <Input type="number" step="0.01" value={l.qty} onChange={(e) => setLine(i, { qty: Number(e.target.value || 0) })} className={`${inputCls} col-span-1 text-right`} />
                <Input type="number" step="0.01" value={l.unit_price} onChange={(e) => setLine(i, { unit_price: Number(e.target.value || 0) })} className={`${inputCls} col-span-3 text-right`} />
                <button type="button" onClick={() => setForm((f) => ({ ...f, lines: f.lines.filter((_, idx) => idx !== i) }))} className="col-span-1 text-[#dc2626] justify-self-center" aria-label="Remove line">
                  <Trash2 size={14} />
                </button>
              </div>
            ))}
          </div>

          {/* Live totals */}
          <div className="mt-6 bg-[#20282c] text-white rounded-md p-5 grid grid-cols-3 gap-4 text-sm">
            <div>Subtotal:<br /><b>{money(totals.sub)}</b></div>
            <div>VAT:<br /><b>{money(totals.vat)}</b></div>
            <div>Gross credit:<br /><b className="text-[#ffc438]">{money(totals.gross)}</b></div>
          </div>
        </section>
      </div>
    </form>
  );
};

export default AdminCreditNoteForm;
